// SocketEvents.ts
export enum SocketEvent {
  // Socket IO Events
  CONNECT = 'connect',
  DISCONNECT = 'disconnect',
  CONNECT_ERROR = 'connect_error',
  OFFLINE = 'offline',

  // Custom Error Event
  ERROR = 'socket_error',

  MESSAGE = 'message',
  // Playlist Events
  SEARCH = 'search',
  ADD_TO_PLAYLIST = 'add_to_playlist',
  REMOVE_FROM_PLAYLIST = 'remove_from_playlist',
  PLAYLIST_UPDATE = 'playlist_update',
  UPDATE_PLAYER_SETTINGS = 'update_player_settings',
  PLAYLIST_PLAY = 'playlist_play',

  // Player Control Events
  PLAYER_PLAY = 'player_play',
  PLAYER_PAUSE = 'player_pause',
  PLAYER_NEXT = 'player_next',

  // Queue ordering + admin PIN (Go server only; Nest never sends ADMIN_STATUS)
  MOVE_PLAYLIST_ITEM = 'move_playlist_item',
  ADMIN_LOGIN = 'admin_login',
  ADMIN_LOGOUT = 'admin_logout',
  ADMIN_SET_PASSWORD = 'admin_set_password',
  ADMIN_STATUS = 'admin_status',

  // Admin -> desktop (Go server, desktop 1.0.29+)
  SEND_REACTION = 'send_reaction',
  // Admin -> desktop: show a short text on the screen (desktop 1.0.32+).
  SEND_SCREEN_MESSAGE = 'send_screen_message',
  FORCE_UPDATE = 'force_update',
  FORCE_UPDATE_STATUS = 'force_update_status',
  // ... add other events as needed
}

export enum SocketType {
  CLIENT = 'client',
  PLAYER = 'player',
}

export interface MessagePayload {
  message: string;
}

export interface SearchEventPayload {
  query: string;
  pageToken?: string;
}

export interface AddToPlaylistEventPayload {
  videoDetail: VideoDetails;
}

export interface RemoveFromPlaylistEventPayload {
  videoIndex: number;
}

export interface VideoDetails {
  id: string;
  title: string;
  channelName: string;
  duration: number; // seconds in number
  thumbnail: string;
  singer?: string;
}

export interface SearchEventResponse {
  items: VideoDetails[];
  nextPageToken?: string;
  error?: string;
}

export interface PlayerMeta {
  isPlaying: boolean;
  next?: number; // If next item is queued, otherwise undefined.
  isAutoPlay: boolean;
  currentIndex: number;
  currentVideo?: VideoDetails;
  currentProgress: number;
  isAdPlaying?: boolean; // The player is showing an ad before/inside currentVideo.
}

export interface PlayerSettings {
  showIdleScreen?: boolean;
  musicPlayControlLocked?: boolean;
  playlistSearchLock?: boolean;
  playlistSelectLock?: boolean;
  playlistDeleteLock?: boolean;
  autoRemovePlayedTracks?: boolean;
  skipDeleteThreshold?: number;
  askWhoSing?: boolean;
  // Queue reordering for non-admins (Go server only; admins always can).
  allowGuestPlayNext?: boolean;
  allowGuestMoveUpDown?: boolean;
  allowGuestMoveTopBottom?: boolean;
}

export interface PlaylistUpdate {
  id: string;
  currentTime: number;
  isPaused: boolean;
  duration: number;
  isAdPlaying?: boolean; // Sent by newer desktop builds; older ones omit it.
}

export interface PlaylistUpdatePayload extends PlaylistUpdate, VideoDetails { }

export interface PlaylistUpdateResponse {
  playerMeta: PlayerMeta;
  playlistQueue: VideoDetails[];
  lastPlayerUpdate: Date;
  playerSettings?: PlayerSettings;
  // Connected desktop app version (Go server). Missing if no desktop is
  // connected or it is 1.0.26 or older (those don't report it).
  playerVersion?: string;
  // Whether a desktop player is connected (Go server). Missing on Nest, where
  // the remote guesses from how recently it heard from the player.
  playerConnected?: boolean;
}

// Desktop progress after FORCE_UPDATE, relayed to the remotes.
export interface ForceUpdateStatus {
  state: 'checking' | 'up-to-date' | 'downloading' | 'installing' | 'failed' | 'unsupported';
  currentVersion: string;
  version?: string;
  message?: string;
}

export type PlayerSettingsPayload = Partial<PlayerSettings>;

export interface MovePlaylistItemPayload {
  videoIndex: number;
  videoId: string; // Guards against the queue having changed meanwhile.
  // next = right after the playing track; top = index 0
  action: 'next' | 'up' | 'down' | 'top' | 'bottom';
}

export interface AdminPasswordPayload {
  password: string; // 4 digits
}

// Sent by the server to each client individually.
export interface AdminStatus {
  isAdmin: boolean;
  hasAdminPassword: boolean;
  // Guest permission switches don't apply (unlocked with the PIN). Missing
  // from older servers.
  exemptFromRestrictions?: boolean;
}

// Rejections from the server (admin-only event, locked setting, bad PIN).
export interface ErrorResponse {
  error?: string;
}

// Mapping interfaces
export interface EventPayloads {
  [SocketEvent.MESSAGE]: MessagePayload;
  [SocketEvent.SEARCH]: SearchEventPayload;
  [SocketEvent.ADD_TO_PLAYLIST]: AddToPlaylistEventPayload;
  [SocketEvent.REMOVE_FROM_PLAYLIST]: RemoveFromPlaylistEventPayload;
  [SocketEvent.PLAYER_NEXT]: null | undefined;
  [SocketEvent.PLAYER_PAUSE]: null | undefined;
  [SocketEvent.PLAYER_PLAY]: null | undefined;
  [SocketEvent.PLAYLIST_PLAY]: number;
  [SocketEvent.PLAYLIST_UPDATE]: PlaylistUpdateResponse;
  [SocketEvent.UPDATE_PLAYER_SETTINGS]: PlayerSettingsPayload;
  [SocketEvent.MOVE_PLAYLIST_ITEM]: MovePlaylistItemPayload;
  [SocketEvent.ADMIN_LOGIN]: AdminPasswordPayload;
  [SocketEvent.ADMIN_LOGOUT]: null | undefined;
  [SocketEvent.ADMIN_SET_PASSWORD]: AdminPasswordPayload;
  [SocketEvent.SEND_REACTION]: { emoji: string };
  [SocketEvent.SEND_SCREEN_MESSAGE]: { text: string };
  [SocketEvent.FORCE_UPDATE]: null | undefined;
}

export interface EventResponses {
  [SocketEvent.MESSAGE]: MessagePayload; // No response expected for message event
  [SocketEvent.SEARCH]: SearchEventResponse;
  [SocketEvent.ADD_TO_PLAYLIST]: void;
  [SocketEvent.REMOVE_FROM_PLAYLIST]: void;
  [SocketEvent.PLAYLIST_UPDATE]: PlaylistUpdateResponse;
  [SocketEvent.CONNECT_ERROR]: string;
  [SocketEvent.ERROR]: string;
  [SocketEvent.PLAYER_NEXT]: void;
  [SocketEvent.PLAYER_PAUSE]: void;
  [SocketEvent.PLAYER_PLAY]: void;
  [SocketEvent.PLAYLIST_PLAY]: void;
  [SocketEvent.UPDATE_PLAYER_SETTINGS]: void;
  [SocketEvent.MOVE_PLAYLIST_ITEM]: ErrorResponse | true;
  [SocketEvent.ADMIN_LOGIN]: { ok: boolean };
  [SocketEvent.ADMIN_LOGOUT]: true;
  [SocketEvent.ADMIN_SET_PASSWORD]: { ok?: boolean } & ErrorResponse;
  [SocketEvent.ADMIN_STATUS]: AdminStatus;
  [SocketEvent.SEND_REACTION]: ErrorResponse | true;
  [SocketEvent.SEND_SCREEN_MESSAGE]: ErrorResponse | true;
  [SocketEvent.FORCE_UPDATE]: ErrorResponse | true;
  [SocketEvent.FORCE_UPDATE_STATUS]: ForceUpdateStatus;
  // ... other event responses
}
